import { useState } from 'react';
import type { ShareColumn } from '../../shared/types';
import { COLUMN_LABELS, SHARE_COLUMNS } from '../../shared/types';
import type { ExportRow } from '../lib/export';
import { ErrorBanner, Modal } from './ui';

export interface ExportSource {
  /** Fetches the rows for a range. The dialog owns the range, so it refetches. */
  load: (from: string, to: string) => Promise<{ rows: ExportRow[]; totals: { minutes: number; amount?: number } }>;
  scopeName: string;
  showAmounts: boolean;
  /** Public views cannot widen the range beyond what the share allows. */
  lockRange?: boolean;
}

const DEFAULT_COLUMNS: ShareColumn[] = ['work_date', 'project', 'title', 'work_type', 'minutes', 'remark'];

export function ExportDialog({
  from: initialFrom,
  to: initialTo,
  source,
  onClose,
}: {
  from: string;
  to: string;
  source: ExportSource;
  onClose: () => void;
}) {
  const available = SHARE_COLUMNS.filter((c) => c !== 'amount' || source.showAmounts);
  const [columns, setColumns] = useState<ShareColumn[]>(
    DEFAULT_COLUMNS.filter((c) => available.includes(c)),
  );
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);
  const [format, setFormat] = useState<'xlsx' | 'csv'>('xlsx');
  const [includeTotals, setIncludeTotals] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (column: ShareColumn) =>
    // Preserve the canonical column order rather than click order.
    setColumns((prev) =>
      prev.includes(column)
        ? prev.filter((c) => c !== column)
        : available.filter((c) => c === column || prev.includes(c)),
    );

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      // SheetJS is ~400kB, so it is only fetched when an export actually runs.
      const [{ exportFilename, exportTimesheet }, { rows, totals }] = await Promise.all([
        import('../lib/export'),
        source.load(from, to),
      ]);
      exportTimesheet({
        columns,
        rows,
        totals: includeTotals ? totals : null,
        filename: exportFilename(source.scopeName, from, to),
        format,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Export timesheet"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void run()}
            disabled={busy || columns.length === 0 || from > to}
          >
            {busy ? 'Preparing…' : `Download .${format}`}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        {!source.lockRange && (
          <div>
            <span className="label">Date range</span>
            <div className="flex items-center gap-2">
              <input
                type="date"
                className="field !w-auto"
                value={from}
                max={to}
                onChange={(e) => setFrom(e.target.value)}
                aria-label="From"
              />
              <span className="text-[var(--color-ink-muted)]">–</span>
              <input
                type="date"
                className="field !w-auto"
                value={to}
                min={from}
                onChange={(e) => setTo(e.target.value)}
                aria-label="To"
              />
            </div>
          </div>
        )}

        <div>
          <span className="label">Columns</span>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {available.map((column) => (
              <label
                key={column}
                className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-[13px] transition-colors ${
                  columns.includes(column)
                    ? 'border-[var(--color-blue-400)] bg-[var(--color-blue-50)]'
                    : 'border-[var(--color-line)] bg-white hover:bg-[var(--color-paper)]'
                }`}
              >
                <input
                  type="checkbox"
                  className="accent-[var(--color-blue-600)]"
                  checked={columns.includes(column)}
                  onChange={() => toggle(column)}
                />
                {COLUMN_LABELS[column]}
              </label>
            ))}
          </div>
          {columns.length === 0 && (
            <p className="mt-2 text-[12px] text-[var(--color-coral-600)]">Pick at least one column.</p>
          )}
        </div>

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="label">Format</span>
            <div className="inline-flex rounded-[var(--radius-pill)] border border-[var(--color-line)] bg-white p-1">
              {(['xlsx', 'csv'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFormat(f)}
                  className={`rounded-[var(--radius-pill)] px-3.5 py-1.5 text-[13px] font-semibold ${
                    format === f ? 'bg-[var(--color-blue-600)] text-white' : 'text-[var(--color-ink-soft)]'
                  }`}
                >
                  .{f}
                </button>
              ))}
            </div>
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              className="accent-[var(--color-blue-600)]"
              checked={includeTotals}
              onChange={(e) => setIncludeTotals(e.target.checked)}
            />
            Include a totals row
          </label>
        </div>
      </div>
    </Modal>
  );
}
